import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputTextComponent } from '../input-text/input-text.component';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

/**
 * A choosable tag for suggestion mode. `count` is shown at the end of its
 * dropdown row; the colours paint both the row and the chip once chosen.
 */
export interface TagsInputSuggestion {
  label: string;
  count?: number;
  backgroundColour?: string;
  fontColour?: string;
}

@Component({
  selector: 'app-tags-input',
  templateUrl: './tags-input.component.html',
  styleUrls: ['./tags-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagsInputComponent),
      multi: true,
    },
  ],
})
export class TagsInputComponent
  implements ControlValueAccessor, OnInit, OnChanges, OnDestroy
{
  @Input() placeholder: string = 'Add a tag...';
  @Input() maxTags: number = 50;
  @Input() maxLength: number = 50;
  @Input() allowDuplicates: boolean = false;
  @Input() label: string = '';
  @Input() required: boolean = false;
  /**
   * Non-null switches on suggestion mode: only these labels can be added, and
   * chips render in this order and in these colours. Null keeps free text.
   */
  @Input() suggestions: TagsInputSuggestion[] | null = null;
  /** Translation key taking {{label}}; offered when no suggestion matches. */
  @Input() createOptionKey: string | null = null;
  /** Translation key shown after a suggestion's count (e.g. "items"). */
  @Input() countLabelKey: string | null = null;
  @Input() hideHint = false;

  @Output() tagAdded = new EventEmitter<string>();
  @Output() tagRemoved = new EventEmitter<string>();
  /** The trimmed typed text, when the create option is chosen. */
  @Output() createRequested = new EventEmitter<string>();

  /** The composed in-chip base primitive; its `bareInput` is the native input. */
  @ViewChild(InputTextComponent) private tagInput?: InputTextComponent;

  tags: string[] = [];
  inputValue: string = '';
  disabled: boolean = false;
  focused: boolean = false;
  /** Suggestion rows currently offered in the dropdown. */
  filteredSuggestions: TagsInputSuggestion[] = [];
  highlightedIndex = 0;
  private destroy$ = new Subject<void>();

  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private translateService: TranslateService) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['suggestions']) {
      this.tags = this.sortBySuggestions(this.tags);
      this.refreshSuggestions();
    }
  }

  get suggestionMode(): boolean {
    return this.suggestions !== null;
  }

  get showCreateOption(): boolean {
    return (
      this.suggestionMode &&
      !!this.createOptionKey &&
      !!this.inputValue.trim() &&
      this.filteredSuggestions.length === 0
    );
  }

  get dropdownOpen(): boolean {
    return (
      this.suggestionMode &&
      this.focused &&
      !this.disabled &&
      this.tags.length < this.maxTags &&
      (this.filteredSuggestions.length > 0 || this.showCreateOption)
    );
  }

  /** The suggestion matching a chip label (case-insensitive), if any. */
  suggestionFor(tag: string): TagsInputSuggestion | undefined {
    const key = tag.toLowerCase();
    return this.suggestions?.find((s) => s.label.toLowerCase() === key);
  }

  /** Empties the typed text. */
  clearInput(): void {
    this.inputValue = '';
    const native = this.tagInput?.bareInput?.nativeElement;
    if (native) {
      native.value = '';
    }
    this.refreshSuggestions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: string[]): void {
    this.tags = this.sortBySuggestions(value || []);
    this.refreshSuggestions();
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Mirrors the former [(ngModel)] binding on the raw input. */
  onInputChange(event: Event): void {
    this.inputValue = (event.target as HTMLInputElement).value;
    this.refreshSuggestions();
  }

  onInputKeyDown(event: KeyboardEvent): void {
    if (this.suggestionMode) {
      this.onSuggestionKeyDown(event);
      return;
    }
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    // Handle Enter, Comma, or Tab to add tag
    if (
      (event.key === 'Enter' || event.key === ',' || event.key === 'Tab') &&
      value
    ) {
      event.preventDefault();
      this.addTag(value);
      input.value = '';
      this.inputValue = '';
    }

    // Handle Backspace to remove last tag when input is empty
    if (event.key === 'Backspace' && !value && this.tags.length > 0) {
      event.preventDefault();
      this.removeTag(this.tags.length - 1);
    }
  }

  onInputBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    // Suggestion mode never adds free text, and keeps what was typed.
    if (value && !this.suggestionMode) {
      this.addTag(value);
      input.value = '';
      this.inputValue = '';
    }

    this.focused = false;
    this.onTouched();
  }

  onInputFocus(): void {
    this.focused = true;
    this.refreshSuggestions();
  }

  /** mousedown + preventDefault keeps focus, so blur does not fire first. */
  onSuggestionMouseDown(event: MouseEvent, suggestion: TagsInputSuggestion) {
    event.preventDefault();
    this.chooseSuggestion(suggestion);
  }

  onCreateMouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.requestCreate();
  }

  addTag(tag: string): void {
    const trimmedTag = tag.trim();

    // Validate tag
    if (!trimmedTag) {
      return;
    }

    // Check max length
    if (trimmedTag.length > this.maxLength) {
      return;
    }

    // Check duplicates (case-insensitive)
    const existingTag = this.tags.find(
      (existingTag) => existingTag.toLowerCase() === trimmedTag.toLowerCase(),
    );

    if (!this.allowDuplicates && existingTag) {
      return;
    }

    // Check max tags
    if (this.tags.length >= this.maxTags) {
      return;
    }

    // Suggestion mode only takes a known label, in its catalog spelling.
    let tagToAdd = trimmedTag;
    if (this.suggestionMode) {
      const suggestion = this.suggestionFor(trimmedTag);
      if (!suggestion) {
        return;
      }
      tagToAdd = suggestion.label;
    }

    this.tags = this.sortBySuggestions([...this.tags, tagToAdd]);
    this.onChange(this.tags);
    this.refreshSuggestions();

    // Emit event for tag addition
    this.tagAdded.emit(tagToAdd);
  }

  removeTag(index: number): void {
    if (this.disabled) {
      return;
    }

    const removedTag = this.tags[index];
    this.tags = this.tags.filter((_, i) => i !== index);
    this.onChange(this.tags);
    this.refreshSuggestions();

    // Emit event for tag removal
    this.tagRemoved.emit(removedTag);
  }

  onContainerClick(): void {
    if (!this.disabled) {
      this.tagInput?.bareInput?.nativeElement.focus();
    }
  }

  private onSuggestionKeyDown(event: KeyboardEvent): void {
    const value = this.inputValue.trim();
    const open = this.dropdownOpen;
    const rows = this.filteredSuggestions.length;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (open && rows > 0) {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.highlightedIndex = Math.min(
          rows - 1,
          Math.max(0, this.highlightedIndex + step),
        );
      }
      return;
    }

    if (event.key === 'Enter' || event.key === ',') {
      if (open && rows > 0) {
        event.preventDefault();
        this.chooseSuggestion(this.filteredSuggestions[this.highlightedIndex]);
      } else if (event.key === 'Enter' && value) {
        // Never submit the surrounding form with unmatched text.
        event.preventDefault();
        if (open && this.showCreateOption) {
          this.requestCreate();
        }
      }
      return;
    }

    if (
      event.key === 'Backspace' &&
      !value &&
      !this.disabled &&
      this.tags.length > 0
    ) {
      event.preventDefault();
      this.removeTag(this.tags.length - 1);
    }
  }

  private chooseSuggestion(suggestion: TagsInputSuggestion): void {
    const before = this.tags.length;
    this.addTag(suggestion.label);
    if (this.tags.length > before) {
      this.clearInput();
    }
  }

  private requestCreate(): void {
    const value = this.inputValue.trim();
    if (value) {
      this.createRequested.emit(value);
    }
  }

  private refreshSuggestions(): void {
    if (!this.suggestions) {
      this.filteredSuggestions = [];
      this.highlightedIndex = 0;
      return;
    }
    const query = this.inputValue.trim().toLowerCase();
    const chosen = new Set(this.tags.map((t) => t.toLowerCase()));
    this.filteredSuggestions = this.suggestions.filter((s) => {
      const label = s.label.toLowerCase();
      return !chosen.has(label) && label.includes(query);
    });
    this.highlightedIndex = 0;
  }

  /** Orders tags by the suggestions' order; unknown labels keep their place at the end. */
  private sortBySuggestions(tags: string[]): string[] {
    if (!this.suggestions) {
      return tags;
    }
    const position = new Map<string, number>();
    this.suggestions.forEach((s, i) => position.set(s.label.toLowerCase(), i));
    const rank = (t: string) =>
      position.get(t.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
    return tags
      .map((tag, i) => ({ tag, i }))
      .sort((a, b) => rank(a.tag) - rank(b.tag) || a.i - b.i)
      .map((e) => e.tag);
  }
}
